import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertTriangle, Zap, BarChart3, Factory, Wrench, Lightbulb, Wind, Thermometer, Building2, Plus, X, Fuel, Sun } from 'lucide-react';
import type { EnergyConsumptionData, EnergySourceUsage, EnergyUsageCategory, EnergySource } from '../types';

interface EnergyConsumptionProps {
  onNext: (data: EnergyConsumptionData) => void;
  onBack: () => void;
  initialData?: EnergyConsumptionData;
  energyMixData?: { [key: string]: number }; // From Energy Profile step
  energySourcesData?: EnergySource[]; // Full energy source data with names
}

// Default usage categories for different energy sources
const DEFAULT_USAGE_CATEGORIES = [
  { name: 'Production Equipment', description: 'Manufacturing machines, assembly lines, processing equipment', icon: 'Factory' },
  { name: 'HVAC Systems', description: 'Heating, ventilation, air conditioning', icon: 'Wind' },
  { name: 'Lighting', description: 'General facility and workspace lighting', icon: 'Lightbulb' },
  { name: 'Office Equipment', description: 'Computers, printers, office appliances', icon: 'Building2' },
  { name: 'Support Systems', description: 'Compressors, pumps, auxiliary equipment', icon: 'Wrench' },
  { name: 'Transportation', description: 'Forklifts, vehicles, material handling', icon: 'Factory' },
  { name: 'Process Heating', description: 'Furnaces, boilers, heat treatment', icon: 'Thermometer' },
  { name: 'Other', description: 'Other specific uses', icon: 'Building2' },
];

export function EnergyConsumption({ onNext, onBack, initialData, energyMixData, energySourcesData }: EnergyConsumptionProps) {
  // Get available energy sources from energy mix
  const availableEnergySources = energyMixData ? Object.keys(energyMixData).filter(source => energyMixData[source] > 0) : [];
  
  // Default energy sources if none provided
  const defaultEnergySources: EnergySource[] = [
    { id: 'electricity', name: 'Electricity', icon: Lightbulb, color: '#3B82F6' },
    { id: 'diesel', name: 'Diesel', icon: Fuel, color: '#EF4444' },
    { id: 'natural-gas', name: 'Natural Gas', icon: Factory, color: '#10B981' },
    { id: 'fuel-oil', name: 'Fuel Oil', icon: Fuel, color: '#F97316' },
    { id: 'solar', name: 'Solar (Existing)', icon: Sun, color: '#22C55E' },
  ];
  
  // Additional energy sources for adding new ones
  const additionalEnergySources: EnergySource[] = [
    { id: 'coal', name: 'Coal', icon: Factory, color: '#6B7280' },
    { id: 'wind', name: 'Wind', icon: Wind, color: '#06B6D4' },
    { id: 'hydro', name: 'Hydro', icon: Zap, color: '#3B82F6' },
    { id: 'biomass', name: 'Biomass', icon: Factory, color: '#22C55E' },
    { id: 'propane', name: 'Propane', icon: Fuel, color: '#8B5CF6' },
    { id: 'steam', name: 'Steam', icon: Thermometer, color: '#06B6D4' },
  ];
  
  // Combine all available energy sources
  const allEnergySources = energySourcesData || defaultEnergySources;
  
  // State for adding new energy sources
  const [showAddEnergySource, setShowAddEnergySource] = useState(false);
  const [customEnergyName, setCustomEnergyName] = useState('');
  const [customEnergySources, setCustomEnergySources] = useState<EnergySource[]>([]);
  const [currentEnergyMix, setCurrentEnergyMix] = useState<{ [key: string]: number }>(energyMixData || {});
  const [customEnergyError, setCustomEnergyError] = useState<string>('');

  // Helper function to check if energy source name is unique
  const isEnergySourceNameUnique = (name: string): boolean => {
    const allNames = [
      ...allEnergySources.map(s => s.name.toLowerCase()),
      ...customEnergySources.map(s => s.name.toLowerCase()),
      ...Object.keys(currentEnergyMix).map(key => getEnergySourceName(key).toLowerCase())
    ];
    return !allNames.includes(name.toLowerCase());
  };
  
  // Helper function to get energy source name
  const getEnergySourceName = (sourceId: string): string => {
    const source = [...allEnergySources, ...customEnergySources].find(s => s.id === sourceId);
    return source ? source.name : sourceId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to get a unique color for custom energy sources
  const getCustomEnergySourceColor = (): string => {
    const colors = [
      '#EC4899', // Pink
      '#8B5CF6', // Purple
      '#06B6D4', // Cyan
      '#F59E0B', // Amber
      '#EF4444', // Red
      '#10B981', // Emerald
      '#F97316', // Orange
      '#84CC16', // Lime
      '#6366F1', // Indigo
      '#EC4899', // Rose
      '#14B8A6', // Teal
      '#F43F5E', // Rose
    ];
    
    // Get the number of existing custom sources to determine color index
    const colorIndex = customEnergySources.length % colors.length;
    return colors[colorIndex];
  };
  
  const [selectedEnergySource, setSelectedEnergySource] = useState<string>(availableEnergySources[0] || '');
  const [energySourceUsages, setEnergySourceUsages] = useState<EnergySourceUsage[]>(() => {
    if (initialData?.energySourceUsages) return initialData.energySourceUsages;
    
    // Initialize with empty usage for each energy source
    return availableEnergySources.map(source => ({
      source,
      categories: []
    }));
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Update energy sources when energyMixData changes (e.g., when user goes back and adds new sources)
  useEffect(() => {
    if (energyMixData) {
      const newAvailableSources = Object.keys(energyMixData).filter(source => energyMixData[source] > 0);
      
      // Update current energy mix
      setCurrentEnergyMix(energyMixData);
      
      // Update energy source usages to include any new sources and remove removed sources
      setEnergySourceUsages(prev => {
        const existingSources = prev.map(usage => usage.source);
        const newSources = newAvailableSources.filter(source => !existingSources.includes(source));
        const removedSources = existingSources.filter(source => !newAvailableSources.includes(source));
        
        // Add new sources with empty categories
        const newUsages = newSources.map(source => ({
          source,
          categories: []
        }));
        
        // Remove sources that are no longer in the energy mix
        const filteredUsages = prev.filter(usage => !removedSources.includes(usage.source));
        
        return [...filteredUsages, ...newUsages];
      });
      
      // Update selected energy source if current one is not available
      if (newAvailableSources.length > 0 && !newAvailableSources.includes(selectedEnergySource)) {
        setSelectedEnergySource(newAvailableSources[0]);
      }
    }
  }, [energyMixData, selectedEnergySource]);

  // Auto-save functionality
  useEffect(() => {
    const timer = setTimeout(() => {
      // Auto-save to localStorage
      const dataToSave = {
        energySourceUsages: energySourceUsages
      };
      localStorage.setItem('energySourceUsages', JSON.stringify(dataToSave));
    }, 1000);

    return () => clearTimeout(timer);
  }, [energySourceUsages]);

  const getCurrentSourceUsage = (): EnergySourceUsage => {
    return energySourceUsages.find(usage => usage.source === selectedEnergySource) || {
      source: selectedEnergySource,
      categories: []
    };
  };

  const updateSourceUsage = (updatedCategories: EnergyUsageCategory[]) => {
    setEnergySourceUsages(prev => prev.map(usage => 
      usage.source === selectedEnergySource 
        ? { ...usage, categories: updatedCategories }
        : usage
    ));
  };

  const addUsageCategory = (categoryName: string, description: string, icon: string) => {
    const currentUsage = getCurrentSourceUsage();
    const newCategory: EnergyUsageCategory = {
      id: crypto.randomUUID(),
      name: categoryName,
      description: description,
      icon: icon,
      percentage: 0
    };
    
    updateSourceUsage([...currentUsage.categories, newCategory]);
  };

  const removeUsageCategory = (categoryId: string) => {
    const currentUsage = getCurrentSourceUsage();
    updateSourceUsage(currentUsage.categories.filter(cat => cat.id !== categoryId));
  };

  const updateCategoryPercentage = (categoryId: string, newPercentage: number) => {
    const currentUsage = getCurrentSourceUsage();
    
    // Calculate current total excluding the category being updated
    const otherCategoriesTotal = currentUsage.categories
      .filter(cat => cat.id !== categoryId)
      .reduce((sum, cat) => sum + cat.percentage, 0);
    
    // Calculate maximum allowed percentage for this category (remaining from 100%)
    const maxAllowed = 100 - otherCategoriesTotal;
    
    // Constrain the new percentage to not exceed the remaining allocation
    const constrainedPercentage = Math.max(0, Math.min(maxAllowed, newPercentage));
    
    const updatedCategories = currentUsage.categories.map(cat =>
      cat.id === categoryId 
        ? { ...cat, percentage: constrainedPercentage }
        : cat
    );
    
    updateSourceUsage(updatedCategories);
  };

  // Function to add a new energy source
  const addEnergySource = (sourceId: string) => {
    // Add to current energy mix with 0% initially
    setCurrentEnergyMix(prev => ({
      ...prev,
      [sourceId]: 0
    }));
    
    // Add to energy source usages
    setEnergySourceUsages(prev => [
      ...prev,
      { source: sourceId, categories: [] }
    ]);
    
    // Select the newly added source
    setSelectedEnergySource(sourceId);
  };

  // Function to add custom energy source
  const addCustomEnergySource = () => {
    if (customEnergyName.trim()) {
      const trimmedName = customEnergyName.trim();
      
      // Check for uniqueness
      if (!isEnergySourceNameUnique(trimmedName)) {
        setCustomEnergyError('This energy source name is already used. Please choose a different name.');
        return;
      }

      // Clear any previous error
      setCustomEnergyError('');

      // Use the name directly as the key
      const newCustomSource: EnergySource = {
        id: trimmedName, // Use name as ID
        name: trimmedName,
        icon: Zap,
        color: getCustomEnergySourceColor()
      };
      
      // Add to custom sources
      setCustomEnergySources(prev => [...prev, newCustomSource]);
      
      // Add to energy mix and usages using the name as key
      addEnergySource(trimmedName);
      
      // Clear input and hide form
      setCustomEnergyName('');
      setShowAddEnergySource(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    
    // Get currently available energy sources from the energy mix
    const availableSources = Object.keys(currentEnergyMix).filter(source => currentEnergyMix[source] > 0);
    
    // Only validate energy sources that are currently in the energy mix
    energySourceUsages
      .filter(usage => availableSources.includes(usage.source))
      .forEach(usage => {
        const sourceName = getEnergySourceName(usage.source);
        if (usage.categories.length === 0) {
          errors[`source-${usage.source}`] = `${sourceName} must have at least one usage category`;
        } else {
          const totalPercentage = usage.categories.reduce((sum, cat) => sum + cat.percentage, 0);
          if (totalPercentage === 0) {
            errors[`source-${usage.source}`] = `${sourceName} categories must have some percentage allocation`;
          } else if (totalPercentage !== 100) {
            if (totalPercentage < 100) {
              errors[`source-${usage.source}`] = `${sourceName} allocation incomplete: ${totalPercentage.toFixed(1)}% (need ${(100 - totalPercentage).toFixed(1)}% more)`;
            } else {
              errors[`source-${usage.source}`] = `${sourceName} allocation exceeds 100%: ${totalPercentage.toFixed(1)}%`;
            }
          }
        }
      });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Only include energy sources that are currently in the energy mix
      const availableSources = Object.keys(currentEnergyMix).filter(source => currentEnergyMix[source] > 0);
      const filteredEnergySourceUsages = energySourceUsages.filter(usage => availableSources.includes(usage.source));
      
      const energyConsumptionData: EnergyConsumptionData = {
        mode: 'monthly',
        rows: [],
        energySourceUsages: filteredEnergySourceUsages
      };
      onNext(energyConsumptionData);
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: any } = {
      Factory, Wrench, Lightbulb, Wind, Thermometer, Building2, Fuel, Sun
    };
    return icons[iconName] || Building2;
  };

  const currentSourceUsage = getCurrentSourceUsage();
  const totalPercentage = currentSourceUsage.categories.reduce((sum, cat) => sum + cat.percentage, 0);
  const remainingPercentage = 100 - totalPercentage;

  return (
    <div className="max-w-4xl mx-auto">
      <style>{`
        .energy-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          border-radius: 4px;
          outline: none;
          transition: all 0.2s;
        }
        
        .energy-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: white;
          border: 3px solid currentColor;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          transition: all 0.2s;
        }
        
        .energy-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        
        .energy-slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: white;
          border: 3px solid currentColor;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          transition: all 0.2s;
        }
        
        .energy-slider::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        
        .energy-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.3), 0 2px 6px rgba(0,0,0,0.15);
        }
      `}</style>
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Energy Usage Breakdown</h2>
        </div>

        <div className="mb-6 bg-blue-50 rounded-lg p-4">
          <p className="text-blue-800 text-sm">
            <strong>Tell us what you spend your energy on:</strong> For each energy source from your Energy Profile, specify what you use it for and what percentage goes to each use. This helps us understand your energy consumption patterns.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Energy Source Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Zap className="h-5 w-5 mr-2 text-blue-600" />
                Select Energy Source
              </h3>
              <button
                type="button"
                onClick={() => setShowAddEnergySource(!showAddEnergySource)}
                className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Energy Source
              </button>
            </div>

            {/* Add Energy Source Form */}
            {showAddEnergySource && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Add New Energy Source</h4>
                
                {/* Predefined Additional Sources */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                  {additionalEnergySources.map(source => {
                    const isAlreadyUsed = Object.keys(currentEnergyMix).includes(source.id);
                    const IconComponent = source.icon;
                    
                    return (
                      <button
                        key={source.id}
                        type="button"
                        onClick={() => !isAlreadyUsed && addEnergySource(source.id)}
                        disabled={isAlreadyUsed}
                        className={`p-2 rounded-lg border text-left text-sm transition-colors ${
                          isAlreadyUsed 
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <IconComponent className={`h-3 w-3 mr-2 ${isAlreadyUsed ? 'text-gray-400' : 'text-blue-600'}`} />
                          <span className="font-medium">{source.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Custom Energy Source Input */}
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-600 mb-2">Or add a custom energy source:</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g., Geothermal, Nuclear..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={customEnergyName}
                      onChange={(e) => {
                        setCustomEnergyName(e.target.value);
                        setCustomEnergyError(''); // Clear error when typing
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && addCustomEnergySource()}
                    />
                    <button
                      type="button"
                      onClick={addCustomEnergySource}
                      disabled={!customEnergyName.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>
                  {customEnergyError && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-xs">
                      {customEnergyError}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.keys(currentEnergyMix).map(source => (
                <button
                  key={source}
                  type="button"
                  onClick={() => setSelectedEnergySource(source)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedEnergySource === source
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 rounded mr-3" 
                      style={{ backgroundColor: getSourceColor(source) }}
                    />
                    <div>
                      <div className="font-medium">
                        {getEnergySourceName(source)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {currentEnergyMix[source]?.toFixed(1) || '0.0'}% of total energy
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Current Energy Source Usage */}
          {selectedEnergySource && (
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <div 
                    className="w-4 h-4 rounded mr-3" 
                    style={{ backgroundColor: getSourceColor(selectedEnergySource) }}
                  />
                  What do you use {getEnergySourceName(selectedEnergySource)} for?
                </h3>
                <div className="text-sm">
                  <span className={`font-semibold ${totalPercentage === 100 ? 'text-green-600' : totalPercentage > 100 ? 'text-red-600' : 'text-yellow-600'}`}>
                    Total: {totalPercentage.toFixed(1)}%
                  </span>
                  {remainingPercentage > 0 && (
                    <span className="text-gray-500 ml-2">
                      ({remainingPercentage.toFixed(1)}% remaining)
                    </span>
                  )}
                  {remainingPercentage < 0 && (
                    <span className="text-red-500 ml-2">
                      ({Math.abs(remainingPercentage).toFixed(1)}% over limit)
                    </span>
                  )}
                </div>
              </div>

              {/* Usage Categories */}
              <div className="space-y-4 mb-6">
                {currentSourceUsage.categories.map((category) => {
                  const IconComponent = getIconComponent(category.icon);
                  
                  // Calculate max allowed for this specific category
                  const otherCategoriesTotal = currentSourceUsage.categories
                    .filter(cat => cat.id !== category.id)
                    .reduce((sum, cat) => sum + cat.percentage, 0);
                  const maxAllowedForCategory = 100 - otherCategoriesTotal;
                  
                  return (
                    <div key={category.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <IconComponent className="h-5 w-5 text-blue-600 mr-3" />
                          <div>
                            <h4 className="font-medium text-gray-900">{category.name}</h4>
                            <p className="text-sm text-gray-600">{category.description}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeUsageCategory(category.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Percentage allocation</span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-900">{category.percentage.toFixed(1)}%</span>
                            {maxAllowedForCategory < 100 && (
                              <div className="text-xs text-gray-500">
                                (max: {maxAllowedForCategory.toFixed(1)}%)
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-gray-500 w-8 text-right">0%</span>
                          <div className="flex-1 relative">
                            <input
                              type="range"
                              min="0"
                              max={maxAllowedForCategory}
                              step="0.5"
                              value={category.percentage}
                              onChange={(e) => updateCategoryPercentage(category.id, Number(e.target.value))}
                              className="w-full energy-slider"
                              style={{
                                background: `linear-gradient(to right, ${getSourceColor(selectedEnergySource)} 0%, ${getSourceColor(selectedEnergySource)} ${(category.percentage / maxAllowedForCategory) * 100}%, #e5e7eb ${(category.percentage / maxAllowedForCategory) * 100}%, #e5e7eb 100%)`,
                                color: getSourceColor(selectedEnergySource)
                              }}
                            />
                            {/* Floating percentage indicator */}
                            <div 
                              className="absolute -top-8 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded pointer-events-none transition-all duration-200"
                              style={{ 
                                left: `${(category.percentage / maxAllowedForCategory) * 100}%`,
                                opacity: category.percentage > 0 ? 1 : 0
                              }}
                            >
                              {category.percentage.toFixed(1)}%
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-800"></div>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500 w-8">{maxAllowedForCategory.toFixed(0)}%</span>
                        </div>
                        <div className="text-xs text-gray-500 text-center">
                          {category.percentage.toFixed(1)}% of {getEnergySourceName(selectedEnergySource)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add Usage Category */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Add Usage Category</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {DEFAULT_USAGE_CATEGORIES.map((category) => {
                    const IconComponent = getIconComponent(category.icon);
                    const isAlreadyAdded = currentSourceUsage.categories.some(cat => cat.name === category.name);
                    
                    return (
                      <button
                        key={category.name}
                        type="button"
                        onClick={() => !isAlreadyAdded && addUsageCategory(category.name, category.description, category.icon)}
                        disabled={isAlreadyAdded}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          isAlreadyAdded 
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <IconComponent className={`h-4 w-4 mr-3 ${isAlreadyAdded ? 'text-gray-400' : 'text-blue-600'}`} />
                          <div>
                            <div className="font-medium text-sm">{category.name}</div>
                            <div className="text-xs text-gray-500">{category.description}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Validation Error for Current Source */}
              {validationErrors[`source-${selectedEnergySource}`] && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">
                    {validationErrors[`source-${selectedEnergySource}`]}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Progress Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
              Progress Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.keys(currentEnergyMix).map(source => {
                const usage = energySourceUsages.find(u => u.source === source);
                const totalPerc = usage?.categories.reduce((sum, cat) => sum + cat.percentage, 0) || 0;
                const isComplete = usage && usage.categories.length > 0 && totalPerc === 100;
                
                return (
                  <div key={source} className={`bg-white rounded-lg p-3 border-2 ${
                    isComplete ? 'border-green-300' : 'border-yellow-300'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded mr-2" 
                          style={{ backgroundColor: getSourceColor(source) }}
                        />
                        <span className="font-medium text-sm">
                          {getEnergySourceName(source)}
                        </span>
                      </div>
                      <span className={`font-bold text-sm ${
                        isComplete ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {totalPerc.toFixed(1)}%
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {usage?.categories.length || 0} categories
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          isComplete ? 'bg-green-500' : totalPerc > 100 ? 'bg-red-500' : 'bg-yellow-500'
                        }`}
                        style={{ width: `${Math.min(totalPerc, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* General Validation Errors */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-red-800">Please complete the following:</h4>
                  <ul className="mt-2 text-sm text-red-700 space-y-1">
                    {Object.values(validationErrors).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={onBack}
              className="bg-gray-100 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg"
            >
              Back
            </button>
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg shadow-lg"
            >
              Continue to Site & Operations
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper function to get consistent colors for energy sources
function getSourceColor(source: string): string {
  const colors: { [key: string]: string } = {
    electricity: '#3B82F6', // Blue
    diesel: '#EF4444',      // Red
    gasoline: '#F59E0B',    // Amber
    'natural-gas': '#10B981', // Emerald
    propane: '#8B5CF6',     // Violet
    'fuel-oil': '#F97316',  // Orange
    coal: '#6B7280',        // Gray
    biomass: '#22C55E',     // Green
    steam: '#06B6D4',       // Cyan
    solar: '#22C55E',       // Green
    other: '#EC4899'        // Pink
  };
  return colors[source] || '#6B7280';
}